
/**
 * Where a pane sits, and what stays rendered behind it (16.102, Don's direction).
 *
 * Extracted from app.ts's render() so the rule is testable. It was previously a
 * pair of inline ternaries, and the defect Don found lived in exactly that
 * pair: a module opened at a dock rendered its own header while the Lot View
 * underneath kept rendering the same header, so the captain saw the ship,
 * location, credits and waypoint twice -- and the copy behind was STALE, since
 * only the pane re-renders when credits change.
 *
 * The rule is not symmetric, and that asymmetry is the design:
 *
 * - **Over the Expedition View** the pane is inset and the View stays mounted.
 *   The captain must be able to see that space carries on around them; nothing
 *   out there is paused, and hiding it would imply otherwise.
 * - **At a Station or Base** the module fills the screen and the Lot View is not
 *   rendered at all. Don's words: "in a Station or Base when we open a module we
 *   aren't worried about what's happening elsewhere in the base."
 *
 * Note that the Lot View is *not rendered*, not merely covered. Covering it
 * would leave the second, stale header alive in the DOM one z-index below --
 * which is the bug, not the fix.
 */

export type TopLevelView = "expedition" | "base" | "station";

export interface PaneLayout {
  /** True when the pane should fill the screen rather than float inset. */
  fullScreen: boolean;
  /** Whether the top-level View underneath should still be rendered. */
  keepViewBehind: boolean;
  /** Label for the only way out. */
  closeLabel: string;
}

export function paneLayoutFor(underlyingView: TopLevelView | null): PaneLayout {
  if (underlyingView === "base" || underlyingView === "station") {
    return {
      fullScreen: true,
      keepViewBehind: false,
      closeLabel: `← Back to ${underlyingView === "base" ? "Base" : "Station"}`
    };
  }
  return { fullScreen: false, keepViewBehind: true, closeLabel: "×" };
}

/**
 * Modules whose UI genuinely IS the popup, so clicking them still opens one
 * rather than navigating (16.102). Everything else with a real screen opens that
 * screen directly, removing the click-a-box-then-press-Open two-step.
 */
export function lotClickOpensPopup(kind: string, built: boolean): boolean {
  if (!built) return true; // an unbuilt lot needs its Build prompt first
  return kind !== "navigate";
}
